// GA4 analytics helper.
// Centralizes gtag/dataLayer event tracking so names stay consistent.

type GtagFn = (...args: unknown[]) => void;

declare global {
  interface Window {
    gtag?: GtagFn;
    dataLayer?: unknown[];
  }
}

const isBrowser = () => typeof window !== 'undefined';

function pushEvent(name: string, params: Record<string, unknown> = {}) {
  if (!isBrowser()) return;
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, params);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name, ...params });
    }
  } catch (e) {
    // never let analytics break the app
    // eslint-disable-next-line no-console
    console.warn('[analytics] event failed', name, e);
  }
}

export const trackEvent = pushEvent;

export function trackPageView(path: string, title?: string) {
  if (!isBrowser()) return;
  pushEvent('page_view', {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: title || document.title,
  });
}

// Funnel events - keep names stable, English, no accents.
export const Analytics = {
  clickStartFree: (location: string) => trackEvent('click_start_free', { location }),
  signUpStarted: (plan?: string) => trackEvent('sign_up_started', { plan: plan || 'light' }),
  signUpCompleted: (plan?: string) => trackEvent('sign_up_completed', { plan: plan || 'light', method: 'email' }),
  planSelected: (planName: string, location?: string) =>
    trackEvent('plan_selected', { plan_name: planName, location: location || 'unknown' }),
  checkoutStarted: (planName: string) => trackEvent('checkout_started', { plan_name: planName }),
  checkoutCompleted: (planName?: string, value?: number) =>
    trackEvent('checkout_completed', {
      plan_name: planName || 'unknown',
      ...(typeof value === 'number' ? { value, currency: 'BRL' } : {}),
    }),
  login: (method = 'email') => trackEvent('login', { method }),
  contactClick: (location: string) => trackEvent('contact_click', { location }),
  contactSubmitted: (subject: string, type: 'chamado' | 'sugestao') =>
    trackEvent('contact_submitted', { subject, ticket_type: type }),
  newsletterSubscribed: (source: string) => trackEvent('newsletter_subscribed', { source }),
  upgradeClicked: (planName: string, location: string) =>
    trackEvent('upgrade_clicked', { plan_name: planName, location }),
  featureUsed: (feature: string, extra?: Record<string, unknown>) =>
    trackEvent('feature_used', { feature, ...(extra || {}) }),
};
